// src/pages/expense/_shared/ExpenseDraftNew.jsx
// ✅ 전 사업 확산 [1] 공통화 — "지출데이터+기안 통합 작성" 페이지의 사업 공통 버전.
// 환경/종합 파일럿(ExpenseEnvTotalDraftNew.jsx)을 그대로 일반화한 것으로, 사업마다
// 달라지는 부분(템플릿 목록/API 경로/구분·내용·분야 필드명/거래처·품목·인건비 패널
// 컴포넌트 등)은 전부 config prop으로 받는다(expenseDraftConfig.js 참고). 각 사업
// 폴더의 실제 페이지 파일은 이 컴포넌트에 자기 config를 넘기는 얇은 래퍼일 뿐이다.
import React, { useEffect, useMemo, useRef, useState } from "react";
import useBackdropClose from "../../../hooks/useBackdropClose";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";
import { draftNumberChangedLine, formatDraftSaveError } from "../../../utils/draftNumberNotice";
import { EXPENSE_TEMPLATES } from "../../../components/preview/expense/expenseTemplates";
import ApprovalLine from "../../../components/approval-line/ApprovalLine";
import ApprovalLineModal from "../../../components/approval-line/ApprovalLineModal";
import ApprovalLineConfirmDialog from "../../../components/approval-line/ApprovalLineConfirmDialog";
import GenericDetailTablePanel from "./GenericDetailTablePanel";
import { VEHICLES } from "./vehicles";
// ✅ "월" 표기 포맷 함수는 구 페이지(DraftDocumentNew.jsx)가 원본 스펙 — 새로
// 옮겨적지 않고 그대로 import해서 동일한 결과를 보장한다.
import {
  formatYearMonth,
  formatPayDate,
  formatDateDots,
  parseFlexibleYearMonth,
  parseFlexibleDate,
} from "../../draft_document/DraftDocumentNew";

const OTHER_CODE = "__other__";
// ✅ 제목이 자동생성되는 템플릿 — 라벨 "제목(자동완성)" + 회색 배경, 직접 수정 시
// titleTouched로 보호(금액 수동 보정과 동일 패턴). computeAutoTitle의 case와
// 반드시 짝이 맞아야 한다(여기 없으면 자동생성 자체가 안 걸림).
const TITLE_AUTOGEN_TEMPLATES = [
  "fuel_cost", "utility_fee", "office_maintenance", "electric_bill", "water_rental",
  "pension", "payroll", "credit_card", "accounting_fee", "biz_license", "biz_report",
  "job_training", "banner_ad", "car_installment", "fund_transfer", "general_purchase",
  "supplies", "supplies_office", "service_contract", "meeting_expense", "parking_fee",
  "corporate_seal", "printer_rental", "donation", "postage", "welfare_points",
  "meal_allowance", "interview_fee", "deduction_refund", "withholding_tax", "server_fee",
];
// ✅ 2026-07-30 방침 정정 — accounting_fee/credit_card의 "2026년 5월" 고정
// 폴백은 구 페이지의 죽은 필드(실입력 경로 없음)가 남긴 버그였지 스펙이
// 아니었다는 판단으로, 재현 대상에서 제외하고 각각 전용 입력(집행월/납부월)을
// 새로 추가했다 — 아래 computeAutoTitle 참고.

// ✅ 유류비(세차비) 전용 — 진입 시 품목 표에 미리 채워두는 기본 2행(2026-07-30
// A안). 수량·단가는 빈칸으로 시작하고 표에서 바로 채워 넣는다(ExpenseItemPanel의
// 인라인 편집). 세차비 행은 금액이 실제로 입력될 때만(0보다 클 때) "세차 포함
// 모드"로 인식된다 — 행 존재 여부가 아니라 금액이 기준(아래 fuelWashIncluded 참고).
const FUEL_COST_INITIAL_ITEMS = [
  { name: "유류비", unit: "L", qty: "", price: "", supply: 0, vat: 0, total: 0 },
  { name: "세차비", unit: "식", qty: "", price: "", supply: 0, vat: 0, total: 0 },
];

// ✅ 인터넷·전화요금 전용 — 진입 시 품목 표에 미리 채워두는 기본 1행. 유류비와
// 달리 수량은 항상 1(식)이라 미리 채워두고, 단가만 입력하면 되게 한다.
const UTILITY_FEE_INITIAL_ITEMS = [
  { name: "인터넷 및 전화요금", unit: "식", qty: "1", price: "", supply: 0, vat: 0, total: 0 },
];

const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
const PAY_METHOD_TO_CODE = { 계좌이체: "transfer", 카드결제: "card", 현금결제: "cash" };

function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)}${units[i]}`;
}

// ✅ 유류비(fuel_cost) 세부내역 자동 조립 — 우측 품목 표에서 품목명에 "유류"가
// 들어간 행을 "단가원*수량L"로, "세차"가 들어간 행이 있으면 ", 세차"를 덧붙인다.
// 통합 페이지 전용(구 페이지 DraftDocumentNew의 유류비는 수동 입력 그대로 유지).
export function buildFuelDetail(itemList) {
  const items = itemList || [];
  const fuelItem = items.find((it) => (it.name || "").includes("유류"));
  // ✅ 수량·단가가 아직 안 채워진 기본 행(금액 0)이면 빈값 — "품목이 비어있으면
  // 빈값" 원칙. 세차비도 존재 여부가 아니라 금액이 실제로 입력됐을 때만(2026-07-30
  // A안) ", 세차"를 붙인다.
  if (!fuelItem || Number(fuelItem.total || 0) <= 0) return "";
  const price = Number(fuelItem.price || 0).toLocaleString();
  const washItem = items.find((it) => (it.name || "").includes("세차") && Number(it.total || 0) > 0);
  return `${price}원*${fuelItem.qty}L${washItem ? ", 세차" : ""}`;
}

// ✅ 제목 자동생성 — 구 페이지(DraftDocumentNew.jsx)의 제목 조립식을 그대로
// 옮긴 것(임의 해석 없이 원본 문구 그대로). code가 TITLE_AUTOGEN_TEMPLATES에
// 없으면 호출 자체를 안 하므로 여기 없는 템플릿은 기존처럼 자유 입력 유지.
// ctx: { extraFields, itemName, vendorName, fuelWashIncluded }
export function computeAutoTitle(code, ctx) {
  const ef = ctx.extraFields || {};
  switch (code) {
    case "fuel_cost": {
      const washSuffix = ctx.fuelWashIncluded ? " 및 세차비" : "";
      return ef.carModel ? `금천일자리주식회사 업무용 차량(${ef.carModel}) 유류비${washSuffix} 지출` : "";
    }
    case "utility_fee":
      return ef.utilityMonth ? `금천일자리주식회사 사무실 ${ef.utilityMonth} 인터넷 및 전화요금 납부` : "";
    case "office_maintenance":
      return ef.maintMonth ? `금천일자리주식회사 사무실 ${ef.maintMonth}분 관리비 납부` : "";
    case "electric_bill":
      return ef.elecMonth ? `금천일자리주식회사 사무실 ${ef.elecMonth}분 전기료 납부` : "";
    case "water_rental":
      return ef.waterMonth ? `금천일자리주식회사 ${ef.waterMonth} 사무실 정수기 렌탈료 납부` : "";
    case "pension":
      return ef.pensionMonth ? `${ef.pensionMonth}분 퇴직연금 납부` : "";
    case "payroll": {
      const month = formatYearMonth(ef.payDate);
      return month ? `${month} 임직원 급여 지급` : "";
    }
    // ✅ credit_card — 구 페이지의 workMonth는 죽은 필드였고 그 결과인 "2026년
    // 5월" 고정 폴백은 버그였지 스펙이 아니다(2026-07-30 정정) — 전용 "납부월"
    // 입력으로 대체, 미입력 시 빈값.
    case "credit_card":
      return ef.cardMonth ? `금천일자리주식회사 ${ef.cardMonth} 신용카드 결제대금 납부` : "";
    // ✅ accounting_fee — 마찬가지로 executionMonth 죽은 필드+폴백을 버리고
    // 전용 "집행월" 입력으로 대체.
    case "accounting_fee":
      return ef.executionMonth ? `금천일자리주식회사 ${ef.executionMonth} 기장수수료 지출` : "";
    case "biz_license":
      return ef.bizTarget ? `${ef.bizTarget} 사업자등록증 발급 비용 지출` : "";
    case "biz_report":
      return ef.bizTarget ? `${ef.bizTarget} 영업신고 수수료 지출` : "";
    case "job_training":
      return ef.trainee ? `금천일자리주식회사 ${ef.trainee} 직무 교육비 지출` : "";
    case "banner_ad":
      return ef.adTarget ? `금천일자리주식회사 ${ef.adTarget} 온라인 배너 광고` : "";
    case "car_installment":
      return ef.installmentRound ? `금천일자리주식회사 업무용 특장 차량 ${ef.installmentRound}회차 할부금 지출` : "";
    case "fund_transfer":
      return ctx.vendorName ? `${ctx.vendorName} 통장 보전일시자금 이체` : "";
    case "general_purchase":
      return ctx.itemName ? `금천일자리주식회사 ${ctx.itemName} 구매` : "";
    case "supplies":
    case "supplies_office":
      return ctx.itemName ? `금천일자리주식회사 소모품(${ctx.itemName}) 구매` : "";
    // ✅ service_contract — 구 페이지는 환경/종합만 접미사를 붙이고 나머지
    // 사업(경영팀 포함)은 계약명 그대로를 제목으로 쓴다.
    case "service_contract":
      return ctx.category === "환경/종합" && ef.contractName
        ? `${ef.contractName} 용역 계약 인지세 납부`
        : (ef.contractName || "");
    // ✅ 아래는 다른 입력값에 의존하지 않는 고정 문구(구 페이지 원문 그대로) —
    // 그래도 "제목이 데이터 제목을 겸한다"는 원칙에 맞춰 자동채움 대상에 포함.
    case "meeting_expense": return "금천일자리주식회사 업무추진 간담회비 지출";
    case "parking_fee": return "금천일자리주식회사 주차비용 지출 건";
    case "corporate_seal": return "금천일자리주식회사 법인인감증명서 발급 비용 지출 건";
    case "printer_rental": return "금천일자리주식회사 사무실 복합기 렌탈료 지출";
    case "donation": return "금천일자리주식회사 기부금 지출";
    case "postage": return "금천일자리주식회사 우편 발송";
    case "welfare_points": return "직원 맞춤형 복지비용(복지포인트) 지급";
    case "meal_allowance": return "금천일자리주식회사 직원 급량비 지출";
    case "interview_fee": return "현장근로자 채용 서류 및 면접심사비 지급";
    case "deduction_refund": return "현장근로자 일용직 공제금 환급 건";
    case "withholding_tax": return "근로소득 원천세 납부";
    case "server_fee": return "금천일자리주식회사 홈페이지 서버이용료 납부";
    default:
      return "";
  }
}

// ✅ 미리보기 values 매핑 — 컴포넌트 밖으로 뺀 순수 함수. previewValues useMemo와
// pickTemplate(종류 선택 직후 팝업을 동기적으로 열 때, React state가 아직 반영되기
// 전이라 방금 계산한 "새 값"을 직접 넘겨야 함) 양쪽에서 공유해서 쓴다. 템플릿별
// detail 표(env_total_supplies/env_total_daily_wage/service_contract_payment/
// service_daily_payroll/contractInfo)는 template_code
// 기준으로 분기하므로 사업이 늘어도(각 사업의 templates가 다른 template_code를
// 쓸 뿐) 이 함수 자체는 그대로 재사용된다.
export function buildPreviewValues(code, ctx) {
  if (code === OTHER_CODE || !code) return {};
  const tpl = EXPENSE_TEMPLATES[code];
  if (!tpl) return {};
  const vendorName = ctx.partnerList.filter((p) => p.name.trim()).map((p) => p.name).join(", ");
  const values = { title: ctx.title, attachments: ctx.attachments };

  tpl.fields.forEach((f) => {
    if (f.type === "table") {
      if (f.key === "detail" && (code === "env_total_supplies" || code === "service_contract_payment")) {
        values.detail = { rows: ctx.itemList.map((it) => [it.name, it.unit, it.qty, it.price, it.supply, it.vat, it.remark]) };
      } else if (f.key === "detail" && (code === "env_total_daily_wage" || code === "service_daily_payroll")) {
        values.detail = { rows: ctx.salaryList.map((s) => [s.name, s.totalSalary, s.incomeTax, s.localTax, s.health, s.care, s.pension, s.employment, s.total]) };
      } else if (f.key === "contractInfo") {
        const no = Number(ctx.extraFields.leaseInstallmentNo) || 0;
        values.contractInfo = { rows: [["리스대금(월)", "477,300원", `${no}/60회차`]] };
      } else if (f.key === "detail" && code === "credit_card") {
        // ✅ 신용카드 결제대금 납부 — 구 페이지도 입력이 아니라 금액 1건짜리 고정
        // 행을 합성한다("결제대금"/일시불=금액/연회비="-"/총계=금액/매월 23일 이체).
        const amt = ctx.totalAmount ? ctx.totalAmount.toLocaleString() : "-";
        values.detail = { rows: [["결제대금", amt, "-", amt, "매월 23일 이체"]] };
      } else if (f.key === "detail" && ctx.genericTableRows) {
        // ✅ 전 사업 확산 [5] — 카페 4개점처럼 템플릿마다 표 열 구성이 제각각인
        // 경우 GenericDetailTablePanel이 채운 원본 행을 그대로 사용(사업별 전용
        // 컬럼 매핑이 필요 없음, 컬럼 수는 tpl.columns와 패널 입력이 항상 일치).
        values.detail = { rows: ctx.genericTableRows };
      } else {
        values[f.key] = { rows: [] };
      }
      return;
    }
    // title은 extraFields가 아니라 컴포넌트의 title state로 관리됨 — 위에서 이미 세팅
    if (f.key === "title") return;
    if (f.key === "vendor") { values.vendor = vendorName; return; }
    if (f.type === "money") { values[f.key] = ctx.totalAmount; return; }
    values[f.key] = ctx.extraFields[f.key] ?? f.default ?? "";
  });
  // ✅ 유류비 intro()가 읽는 carNumber/carModel은 tpl.fields에 없는 값(PDF 본문에
  // 별도 행으로 노출하지 않고 도입문에만 쓰기 위해 구 페이지와 동일하게 fields
  // 목록 밖에 둠) — buildPreviewValues 밖에서 값이 안 채워지므로 여기서 직접 주입.
  // washIncluded는 "사용내역 내용" 선택("유류비"/"유류비, 세차비")에 따라 도입문에
  // "및 세차비"를 넣을지 결정 — 구 페이지는 이 선택 자체가 없어 항상 undefined(=
  // 기존 문구 그대로)이므로 회귀 없음. detail은 품목 표에서 자동 조립(수동 입력 아님).
  if (code === "fuel_cost") {
    values.carNumber = ctx.extraFields.carNumber || "";
    values.carModel = ctx.extraFields.carModel || "";
    // ✅ "품목이 진실의 원천" — 세차 포함 여부는 사용내역 내용 텍스트가 아니라
    // 품목 표 자체에서 판정(사용내역 내용은 사용자가 자유롭게 고쳐 써도 되는
    // 필드라 신뢰 기준으로 삼지 않는다).
    values.washIncluded = (ctx.itemList || []).some((it) => (it.name || "").includes("세차"));
    values.detail = buildFuelDetail(ctx.itemList);
  }
  // ✅ 인터넷·전화요금 납기일 — 저장값은 DueDateSplitFields가 조립하는 "YYYY-MM-DD"
  // 그대로 두고(내부 형식 무관), 미리보기/PDF 표기만 공문서 날짜 관례("2026. 6. 1.",
  // 앞자리 0 없음+끝에 마침표)로 변환. formatDateDots가 정확히 이 형식.
  if (code === "utility_fee") values.dueDate = formatDateDots(ctx.extraFields.dueDate);
  // ✅ "월" 표기가 intro에서만 쓰이고 tpl.fields엔 없는 템플릿들 — 구 페이지의
  // 전용 상태(maintMonth/elecMonth/pensionMonth/waterMonth)나 파생값
  // (payroll=formatYearMonth(지급일))을 그대로 재현. utility_fee/credit_card/
  // accounting_fee는 2026-07-30 결정으로 "죽은 필드+고정 폴백"(구 페이지의
  // 버그이지 스펙이 아니었음)을 버리고 전용 입력으로 전환 — 틀린 월이 나가는
  // 것보다 미입력 시 빈값이 안전하다는 판단(폴백 전부 제거).
  if (code === "office_maintenance") values.month = ctx.extraFields.maintMonth || "";
  if (code === "electric_bill") values.month = ctx.extraFields.elecMonth || "";
  if (code === "pension") values.month = ctx.extraFields.pensionMonth || "";
  if (code === "water_rental") values.month = ctx.extraFields.waterMonth || "";
  if (code === "utility_fee") values.month = ctx.extraFields.utilityMonth || "";
  if (code === "accounting_fee") values.month = ctx.extraFields.executionMonth || "";
  if (code === "payroll") {
    values.payDate = formatPayDate(ctx.extraFields.payDate);
    values.month = formatYearMonth(ctx.extraFields.payDate);
  }
  // ✅ 새 입력 없이 이미 다른 용도로 관리되는 값에서 그대로 파생 — 구 페이지도
  // 동일하게 별도 입력을 받지 않고 재사용한다.
  // - service_contract: 사업(카테고리)에 따라 조사가 달라짐(환경/종합만 "체결
  //   하고자" 문구가 다름) — 사업 자체는 사용자 입력이 아니라 이 페이지가 어느
  //   사업(config.category)인지로 고정.
  // - general_purchase/supplies_office/supplies/postage: "사용내역 내용"
  //   (품목형은 품목 표에서, 자유형은 직접 입력)을 그대로 재사용.
  // - fund_transfer: "거래처"(VendorPanel)를 지점명으로 재사용 — 이 템플릿은
  //   fields에 vendor가 따로 없어 값이 안 채워지고 있었다.
  if (code === "service_contract") values.category = ctx.category || "";
  if (["general_purchase", "supplies_office", "supplies", "postage"].includes(code)) {
    values.item = ctx.itemName || "";
  }
  if (code === "fund_transfer") values.fullBranch = vendorName;
  // ✅ 도입문 전용 신규 입력 필드(구 페이지의 전용 상태와 동일 이름으로 매핑).
  if (code === "donation") {
    values.reason = ctx.extraFields.donationReason || "";
    values.target = ctx.extraFields.donationTarget || "";
  }
  if (code === "corporate_seal") values.reason = ctx.extraFields.sealReason || "";
  if (code === "biz_license" || code === "biz_report") values.target = ctx.extraFields.bizTarget || "";
  if (code === "biz_report") values.bizCategory = ctx.extraFields.bizCategory || "";
  if (code === "parking_fee") {
    values.branch = ctx.extraFields.parkingBranch || "";
    values.reason = ctx.extraFields.parkingReason || "";
  }
  if (code === "job_training") values.trainee = ctx.extraFields.trainee || "";
  if (code === "banner_ad") values.adTarget = ctx.extraFields.adTarget || "";
  // ✅ 신용카드 결제대금 납부 — cardNo는 고정 카드번호 상수(구 페이지와 동일,
  // 입력 아님). month는 구 페이지의 workMonth(죽은 필드+"2026년 5월" 폴백)를
  // 그대로 재현하던 것을 2026-07-30 결정으로 버리고 전용 "납부월" 입력으로 전환.
  if (code === "credit_card") {
    values.cardNo = "5794, 8024";
    values.month = ctx.extraFields.cardMonth || "";
  }
  return values;
}

// ✅ 전역 button{color:white} 누수 방어 — Login/Signup.module.css의 스코프 안 되는
// 요소 선택자(button{...})가 CSS Modules에서 앱 전역으로 새는 반복 이슈(CLAUDE.md 기록).
// 이 페이지의 버튼은 전부 인라인 style에 color를 명시해야 흰 배경에서 글자가 보인다 —
// width/height만 명시하던 기존 관례와 달리 color도 새 대상이라 여기서 공통 상수로 관리.
const BTN_TEXT_COLOR = "#222";
const PICK_BTN_STYLE = {
  padding: "16px 8px",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 8,
  cursor: "pointer",
  color: BTN_TEXT_COLOR,
};
// ✅ 상단 "← 종류 다시 선택"/"이전 자료 불러오기" — 내용 크기 기반(width: auto,
// 가로 패딩 16~24px), 화면 폭으로 늘어나지 않도록 명시 지정(CLAUDE.md 버튼 크기 규칙).
const HEADER_BTN_STYLE = {
  width: "auto",
  flexShrink: 0,
  padding: "8px 20px",
  fontSize: 14,
  whiteSpace: "nowrap",
  color: BTN_TEXT_COLOR,
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: 6,
  cursor: "pointer",
};
// ✅ 왼쪽 폼 컬럼 폭에 맞춘 버튼(이전 자료 불러오기) — HEADER_BTN_STYLE과 같은 방식
// (색상 명시로 전역 button{} 누수 방어), 폭만 컬럼에 맞춰 100%.
const FORM_WIDTH_BTN_STYLE = {
  width: "100%",
  padding: "8px 0",
  fontSize: 14,
  color: BTN_TEXT_COLOR,
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: 6,
  cursor: "pointer",
  marginBottom: 10,
};
// ✅ 상단 버튼 그룹의 최종 액션(저장 및 상신) — 다른 상단 버튼과 같은 내용 크기 기반
// 규격(HEADER_BTN_STYLE)이되, 파란 배경으로 강조. marginLeft는 렌더 시 별도 지정(간격 구분).
const SAVE_HEADER_BTN_STYLE = {
  ...HEADER_BTN_STYLE,
  color: "#fff",
  background: "#0b5fff",
  border: "none",
  fontWeight: 600,
};

// ✅ 붙임 행 — 이름 입력만 가변폭(flex:1)이고 나머지(첨부 상태 텍스트/파일첨부
// 버튼·파일명 pill/부수/단위/삭제)는 행마다 동일한 고정폭 컬럼으로 둬서 여러 행이
// 세로로 나열될 때 컬럼끼리 줄이 맞도록 한다.
const ATTACHMENT_STATUS_COL_WIDTH = 92;
const ATTACHMENT_ACTION_COL_WIDTH = 200;
const ATTACHMENT_COUNT_COL_WIDTH = 50;
const ATTACHMENT_UNIT_COL_WIDTH = 18;
const ATTACHMENT_REMOVE_COL_WIDTH = 28;
// ✅ 붙임 영역 전체(헤더 버튼 + 행 요소) 공통 높이 규격 — input/button/label마다
// 기본 padding이 제각각이라 height만 같게 줘도 box-sizing:border-box가 없으면
// 실측 높이가 어긋난다. 모든 요소에 height+boxSizing을 함께 지정해야 줄이 맞는다.
const ATTACHMENT_ROW_HEIGHT = 36;
const ATTACHMENT_ROW_ITEM_STYLE = { height: ATTACHMENT_ROW_HEIGHT, boxSizing: "border-box" };

// ✅ 왼쪽 폼 컬럼 라벨/입력 스타일 — 경영팀 지출 페이지(ExpenseBusinessNew.module.css의
// .row/.rowLabel/.rowField)와 동일 규격(테두리·둥근 모서리·패딩·고정폭 130px·라벨 가운데
// 정렬). 이 페이지는 CSS 모듈이 아니라 인라인 style이라 상수로 정의해 Row에서 일괄 적용.
const ROW_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "130px 1fr",
  gap: "8px 12px",
  alignItems: "stretch",
  marginBottom: 5,
  minHeight: 36,
};
const ROW_LABEL_STYLE = {
  fontWeight: 600,
  color: "#374151",
  fontSize: 14,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#f9fafb",
  boxSizing: "border-box",
  height: 36,
  padding: "0 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "nowrap",
};
const ROW_INPUT_STYLE = {
  width: "100%",
  height: 36,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "0 10px",
  fontSize: 14,
  lineHeight: "1.4",
  boxSizing: "border-box",
};
const ROW_INPUT_READONLY_STYLE = { ...ROW_INPUT_STYLE, backgroundColor: "#e5e7eb" };
// ✅ 자동생성되었지만 직접 수정 가능한 필드(제목/사용내역 내용 등) — 회색은
// READONLY_STYLE과 같지만 readOnly 속성은 안 붙인다(수정하면 흰 배경으로 전환).
const ROW_INPUT_AUTO_STYLE = { ...ROW_INPUT_STYLE, backgroundColor: "#f3f4f6" };
const ROW_TEXTAREA_STYLE = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "4px 8px",
  fontSize: 14,
  lineHeight: "1.3",
  boxSizing: "border-box",
};

// ✅ 유류비 차량번호/차종 자동완성 드롭다운 — VendorPanel/ExpenseItemPanel의
// 자동완성과 같은 톤(부분일치 목록, 클릭 선택, 목록 밖 자유 입력 유지)이지만
// 이 폼 왼쪽 컬럼은 overflow-clip 컨테이너가 아니라 portal 없이 position:absolute로
// 충분하다.
const CAR_SUGGEST_LIST_STYLE = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: 2,
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: 4,
  maxHeight: 180,
  overflowY: "auto",
  zIndex: 20,
  boxShadow: "0 4px 6px rgba(0,0,0,0.15)",
  listStyle: "none",
  padding: 0,
  margin: 0,
};
const CAR_SUGGEST_ITEM_STYLE = {
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: 13,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #f3f4f6",
};

export default function ExpenseDraftNew({ config }) {
  const copyBackdrop = useBackdropClose(() => setShowCopyModal(false));

  const {
    category, businessLabel, draftNoPrefix, dataApiBase, combinedCreateApi,
    listRoutePath, previewRoutePath, templates, categoryItemFieldDefaults,
    itemPanelTemplates, salaryPanelTemplates, genericTablePanelTemplates, fieldNameMap,
    VendorPanel, ExpenseItemPanel, SalaryPanel, otherCodeLabel,
    previewStorageKey, previewChannelName, listRefreshChannelName, previewWindowName,
    // ✅ expenseTemplates.js는 여러 사업이 같은 템플릿(예: fuel_cost)을 공유하는데,
    // 구 페이지(DraftDocumentNew.jsx)는 카테고리별로 일부 필드 기본값을 다르게
    // 덮어쓰는 경우가 있다(예산과목 등). 통합 페이지에도 동일한 사업별 예외가
    // 필요할 때 이 맵으로 지정 — { [templateCode]: { [fieldKey]: overrideDefault } }.
    // 지정 안 하면(기본 {}) 기존처럼 tpl.fields의 default 그대로 사용.
    templateFieldOverrides = {},
  } = config;

  const navigate = useNavigate();

  const [step, setStep] = useState("pick"); // "pick" | "form"
  const [templateCode, setTemplateCode] = useState(""); // EXPENSE_TEMPLATES 키, 기타는 OTHER_CODE

  // 공통 필드
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [executionDate, setExecutionDate] = useState(() => toISO(new Date()));
  const [title, setTitle] = useState("");
  const [supplyAmount, setSupplyAmount] = useState("");
  const [vat, setVat] = useState("");
  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");
  const [remark, setRemark] = useState("");

  // 구분/내용/분야 (자유수정 가능, 템플릿 선택 시 기본값 seed)
  const [categoryName, setCategoryName] = useState("");
  const [itemName, setItemName] = useState("");
  const [fieldName, setFieldName] = useState("");

  // 거래처
  const [partners, setPartners] = useState([]); // 전체 거래처 목록(자동완성용)
  const [partnerList, setPartnerList] = useState([{ name: "", id: null, show: false, highlight: -1 }]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // 품목/인건비 패널
  const [itemList, setItemList] = useState([]);
  const [salaryList, setSalaryList] = useState([]);
  // ✅ 전 사업 확산 [5] — item/salary 패널 둘 다 안 쓰는 표(detail type:"table")
  // 템플릿용 범용 행 입력(GenericDetailTablePanel). 열 구성이 템플릿마다 달라
  // itemList/salaryList처럼 고정 스키마로 못 다루므로 원본 문자열 행 배열 그대로.
  const [genericTableRows, setGenericTableRows] = useState([]);

  // 템플릿별 소규모 부가 필드(예산과목/계약명/교육과정 등) — field.key를 그대로 키로 사용
  const [extraFields, setExtraFields] = useState({});
  const setExtraField = (key, value) => setExtraFields((prev) => ({ ...prev, [key]: value }));

  // ✅ 유류비 차량 — 번호/차종을 "차량" 한 칸으로 통합 입력("104머9770(코란도)"
  // 형식). extraFields.carInput은 입력창에 그대로 보이는 원문, carNumber/carModel은
  // 거기서 파싱해 내부적으로 분리 보관(intro/제목 조립은 기존 로직 그대로 재사용).
  // 목록 밖 차량(렌트 등)은 자유 입력 그대로 허용 — 자동완성은 어디까지나 보조.
  const parseVehicleInput = (text) => {
    const t = (text || "").trim();
    const m = t.match(/^([^(]*)(?:\(([^)]*)\))?/);
    return { carNumber: (m?.[1] || "").trim(), carModel: (m?.[2] || "").trim() };
  };
  const [carSuggestOpen, setCarSuggestOpen] = useState(false);
  // ✅ 품목명 자동완성(ExpenseItemPanel.jsx의 nameHighlight)과 동일한 키보드
  // 내비게이션 패턴 — ↓/↑로 이동, Enter로 선택, Esc로 닫기.
  const [carHighlight, setCarHighlight] = useState(-1);
  // ✅ 포커스만 줘도(입력값 없어도) 4대 전체를 후보로 보여준다 — 차량이 4대뿐이라
  // 전체가 한눈에 보이는 게 한 글자씩 쳐야 뜨는 것보다 빠르다. 입력을 시작하면
  // 기존대로 부분일치로 좁혀진다.
  const carSuggestVehicles = (() => {
    if (!carSuggestOpen) return [];
    const q = (extraFields.carInput || "").trim();
    if (!q) return VEHICLES;
    return VEHICLES.filter((v) => v.number.includes(q) || v.model.includes(q));
  })();
  const selectVehicle = (v) => {
    setExtraFields((prev) => ({ ...prev, carInput: `${v.number}(${v.model})`, carNumber: v.number, carModel: v.model }));
    setCarSuggestOpen(false);
    setCarHighlight(-1);
  };
  const handleCarInputChange = (value) => {
    const { carNumber, carModel } = parseVehicleInput(value);
    setExtraFields((prev) => ({ ...prev, carInput: value, carNumber, carModel }));
    setCarHighlight(-1);
  };
  const handleCarKeyDown = (e) => {
    if (!carSuggestOpen || !carSuggestVehicles.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCarHighlight((i) => (i < carSuggestVehicles.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCarHighlight((i) => (i > 0 ? i - 1 : carSuggestVehicles.length - 1));
    } else if (e.key === "Enter") {
      if (carHighlight >= 0) {
        e.preventDefault();
        selectVehicle(carSuggestVehicles[carHighlight]);
      }
    } else if (e.key === "Escape") {
      setCarSuggestOpen(false);
      setCarHighlight(-1);
    }
  };

  // ✅ 제목/사용내역 내용 자동생성 게이트 — 유류비 전용. 값을 직접 수정하면
  // 이후 자동 재생성(품목 표/차종 변경)이 덮어쓰지 않는다(금액 수동 보정과 동일 패턴).
  const [titleTouched, setTitleTouched] = useState(false);
  const [itemNameTouched, setItemNameTouched] = useState(false);

  // 붙임
  const [attachments, setAttachments] = useState([]);

  // 결재라인
  const [approvers, setApprovers] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);

  // ✅ PDF 미리보기 팝업 — 같은 창을 재사용(이름 고정)하고, BroadcastChannel로
  // 실시간 동기화 + "이탈/저장 완료 시 안내로 전환"을 지원한다.
  const previewWindowRef = useRef(null);
  const previewChannelRef = useRef(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    previewChannelRef.current = new BroadcastChannel(previewChannelName);
    return () => previewChannelRef.current?.close();
  }, [previewChannelName]);

  // ✅ 페이지 이탈(언마운트) 시 열려있는 미리보기 창을 "작성 중인 문서가 없습니다"로 전환.
  // 마운트 시 1회만 등록되는 cleanup이라 별도 useEffect로 분리(다른 값 변경엔 반응 안 함).
  useEffect(() => {
    return () => {
      try {
        new BroadcastChannel(previewChannelName).postMessage({ type: "clear" });
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 이전 자료 불러오기
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySearch, setCopySearch] = useState("");
  const [copyList, setCopyList] = useState([]);
  const [copyLoading, setCopyLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [seq, setSeq] = useState("001");

  // ✅ 첨부파일 업로드(DraftAttachment) — 문서 ID가 있어야 업로드 가능(POST /draft/attachments/
  // 는 document FK 필수)하므로, 파일 자체는 저장 전엔 프론트 state(File 객체)로만 들고
  // 있다가 "저장 및 상신" 시 ①기안 문서 생성 → ②보관 중이던 파일 업로드 순으로 처리한다.
  // 일부 실패 시 문서는 저장된 상태로 두고(재생성 안 함) 실패한 파일만 재시도할 수 있게
  // savedDraftId를 기억해둔다.
  const [savedDraftId, setSavedDraftId] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [failedUploads, setFailedUploads] = useState([]); // [{id, name, file}]
  const fileInputRef = useRef(null);

  // ✅ 거래처 목록 로드 — 최초 마운트 시 + 아래 focus 리스너(거래처 DB 입력 새 창에서
  // 돌아왔을 때)에서 재사용하도록 함수로 분리.
  async function loadVendors() {
    try {
      setLoadingVendors(true);
      const { data } = await api.get("vendors/");
      setPartners(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 거래처 목록 불러오기 실패:", err);
    } finally {
      setLoadingVendors(false);
    }
  }
  useEffect(() => { loadVendors(); }, []);

  // ✅ 품목/내용명 코드(ExpenseItem, 1000~1999) 목록 — ExpenseItemPanel의 "품목명"
  // 자동완성용. 거래처와 동일하게 부모(이 페이지)가 들고 props로 내려준다.
  const [itemCodes, setItemCodes] = useState([]);
  async function loadItemCodes() {
    try {
      const { data } = await api.get("/expense/usage/item/");
      setItemCodes(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 사용내역 내용명 코드 불러오기 실패:", err);
    }
  }
  useEffect(() => { loadItemCodes(); }, []);

  // ✅ 거래처 DB 입력/품목 DB 입력(새 창) 대응 — 팝업 닫힘 감지/BroadcastChannel 대신
  // "이 창(작성 페이지)이 포커스를 다시 받을 때" 재조회하는 방식을 택함. 이유: (1) 등록은
  // VendorRegister.jsx/ExpenseUsageCodes.jsx(각각 거래처 관리·사용내역 코드 관리 전체가
  // 공유하는 컴포넌트/페이지)에서 일어나므로, BroadcastChannel 신호를 쓰려면 이 기능과
  // 무관한 공용 컴포넌트를 수정해야 해 범위가 넓어진다. (2) 팝업 닫힘 감지(window.closed
  // 폴링)는 "등록만 하고 새 창은 계속 열어둔 채 작성 페이지로 돌아와 확인"하는 흐름을
  // 놓친다. (3) focus 리스너는 이 페이지 쪽 코드 몇 줄로 끝나고, 닫힘 여부와 무관하게
  // "창을 다시 보는 순간" 항상 최신 목록을 보장한다 — GET 한 번이라 비용도 거의 없다.
  useEffect(() => {
    const handleFocus = () => { loadVendors(); loadItemCodes(); };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // ✅ 기안 문서 채번(next-seq) — 기존 DraftDocumentNew.jsx와 동일한 관례 재사용.
  // keep-alive 탭이라 재방문 시 마운트가 유지되므로 탭 활성화 때마다 다시 조회한다
  // (처음 조회값에 멈춰 있으면 그 사이 저장된 번호를 몰라 번호가 갈라진다).
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/draft/documents/next-seq/", {
          params: { date: draftDate, category, document_type: "expense_draft" },
        });
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (err) {
        console.error("❌ next-seq 오류:", err);
      }
    })();
    return () => { ignore = true; };
  }, [draftDate, category, seqRefreshTick]);

  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);
  const draftNo = useMemo(() => `${draftNoPrefix}-${yymmdd}-${seq}호`, [draftNoPrefix, yymmdd, seq]);

  // ✅ 품목/인건비 패널 입력 → 공급가/부가세 자동 합산(다른 지출 페이지들과 동일 관례)
  useEffect(() => {
    if (!itemPanelTemplates.includes(templateCode) && !salaryPanelTemplates.includes(templateCode)) return;
    let supply = 0;
    let vatSum = 0;
    itemList.forEach((it) => { supply += Number(it.supply || 0); vatSum += Number(it.vat || 0); });
    salaryList.forEach((s) => { supply += Number(s.totalSalary || 0); });
    setSupplyAmount(String(supply));
    setVat(String(vatSum));
  }, [itemList, salaryList, templateCode, itemPanelTemplates, salaryPanelTemplates]);

  const totalAmount = useMemo(() => (Number(supplyAmount) || 0) + (Number(vat) || 0), [supplyAmount, vat]);

  // ✅ 사용내역 내용 — 품목형 템플릿은 품목 표의 품목명, 급여형 템플릿은 인건비 명단의
  // 이름에서 자동 파생(1건/1명이면 그대로, 복수면 "{첫 항목} 외 N건/명"). 표 행
  // 추가/수정/삭제할 때마다(itemList/salaryList 변경) 실시간 갱신된다. 표가 아직
  // 비어있으면(종류 선택 직후 등) pickTemplate이 이미 채워둔 템플릿 기본값(예:
  // 차량리스료 "포터 리스료")을 지운다 — 그 기본값으로 되돌린다. "기타"는 해당
  // 표가 없으므로 자동 파생 대상이 아니고 기존처럼 자유 입력 유지.
  useEffect(() => {
    // ✅ 유류비는 전용 규칙(품목 표의 "세차" 포함 여부 → "유류비"/"유류비, 세차비")을
    // 아래 별도 effect가 담당 — 여기서는 다른 품목형 템플릿과 동일하게 취급하지 않는다.
    // ✅ 인터넷·전화요금은 품목명(기본 "인터넷 및 전화요금")과 사용내역 내용
    // (기존 "인터넷 및 전화 요금")이 서로 다른 별개 값 — 품목 표를 켜도 사용내역
    // 내용은 pickTemplate이 채운 기존 값 그대로 유지(기존 데이터 연속성).
    if (templateCode === "fuel_cost" || templateCode === "utility_fee") return;
    if (itemPanelTemplates.includes(templateCode)) {
      if (itemList.length === 0) setItemName(categoryItemFieldDefaults[templateCode]?.item_name || "");
      else if (itemList.length === 1) setItemName(itemList[0].name || "");
      else setItemName(`${itemList[0].name || ""} 외 ${itemList.length - 1}건`);
    } else if (salaryPanelTemplates.includes(templateCode)) {
      if (salaryList.length === 0) setItemName(categoryItemFieldDefaults[templateCode]?.item_name || "");
      else if (salaryList.length === 1) setItemName(salaryList[0].name || "");
      else setItemName(`${salaryList[0].name || ""} 외 ${salaryList.length - 1}명`);
    }
  }, [templateCode, itemList, salaryList, itemPanelTemplates, salaryPanelTemplates, categoryItemFieldDefaults]);

  // ✅ 유류비 전용 — "품목이 진실의 원천": 세차비 행이 "존재"하는 게 아니라 그
  // 행에 금액이 실제로 입력됐을 때만(2026-07-30 A안, 금액>0) 세차 포함 모드.
  // 기본 2행 중 세차비 행이 빈 채로 있으면 유류비 단독 모드로 유지된다.
  // 사용내역 내용/제목/도입문 전부 이 값 하나로 갈린다.
  const fuelWashIncluded = templateCode === "fuel_cost"
    && itemList.some((it) => (it.name || "").includes("세차") && Number(it.total || 0) > 0);
  // ✅ fund_transfer 제목("{거래처} 통장 보전일시자금 이체")이 쓰는 거래처명 —
  // buildPreviewValues 안의 동명 지역변수와 동일한 계산(component 스코프에도 필요).
  const vendorName = partnerList.filter((p) => p.name.trim()).map((p) => p.name).join(", ");

  // ✅ 사용내역 내용 자동 표시(회색) — 직접 수정하면(itemNameTouched) 품목 표가
  // 바뀌어도 더 이상 덮어쓰지 않는다(제목과 동일한 보호 패턴).
  useEffect(() => {
    if (templateCode !== "fuel_cost" || itemNameTouched) return;
    setItemName(fuelWashIncluded ? "유류비, 세차비" : "유류비");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateCode, fuelWashIncluded, itemNameTouched]);

  // ✅ 지출방법(extraFields.method) → 거래방법 라디오 자동 연동. extraFields.method는
  // 템플릿 자동 채움(pickTemplate의 setExtraFields)과 사용자 직접 수정(setExtraField)
  // 양쪽 다 이 값을 바꾸므로, 이 값 하나만 지켜보면 두 트리거를 전부 커버한다.
  // 라디오를 수동으로 바꿔도 이 effect는 method 값이 "다시" 바뀔 때만 재실행되므로
  // 수동 선택이 유지되다가, 지출방법이 또 바뀌면 그때 재연동된다.
  useEffect(() => {
    const m = extraFields.method || "";
    if (m.includes("카드")) setPayMethodLabel("카드결제");
    else if (m.includes("현금")) setPayMethodLabel("현금결제");
    else if (m.includes("계좌") || m.includes("이체")) setPayMethodLabel("계좌이체");
    // 매칭 안 되면(예: 계약인지세의 "수의계약..." 문구) 현재 선택 그대로 둔다.
  }, [extraFields.method]);

  // ✅ 제목 자동생성 — TITLE_AUTOGEN_TEMPLATES에 등록된 템플릿만, computeAutoTitle
  // (구 페이지 조립식 그대로)로 재생성. 직접 수정한 뒤(titleTouched)에는 관련
  // 입력이 바뀌어도 더 이상 덮어쓰지 않는다.
  useEffect(() => {
    if (!TITLE_AUTOGEN_TEMPLATES.includes(templateCode) || titleTouched) return;
    setTitle(computeAutoTitle(templateCode, { extraFields, itemName, vendorName, fuelWashIncluded, category }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateCode, extraFields, itemName, vendorName, fuelWashIncluded, titleTouched, category]);

  // ── 종류 선택 ──────────────────────────────────────────────
  // ✅ 종류 선택은 버튼 클릭(사용자 제스처)의 동기 콜스택 안에서 실행된다 — 여기서
  // window.open을 "동기적으로" 호출해야 팝업 차단기에 안 걸린다(비동기 처리 뒤에 열면
  // 차단됨). React state(setTemplateCode 등)는 비동기로 반영되므로 previewValues
  // 메모를 그대로 쓰면 이전 템플릿 값이 찍힌다 — 여기서 새 값을 직접 계산해 넘긴다.
  function pickTemplate(code) {
    setTemplateCode(code);

    let freshTitle = "";
    let freshExtra = {};
    let freshAttachments = [];

    if (code !== OTHER_CODE) {
      const d = categoryItemFieldDefaults[code] || {};
      setCategoryName(d.category_name || "");
      setItemName(d.item_name || "");
      setFieldName(d.field_name || "");

      const tpl = EXPENSE_TEMPLATES[code];
      const seedExtra = {};
      (tpl?.fields || []).forEach((f) => {
        if (f.default && !["title", "amount", "vendor"].includes(f.key)) {
          seedExtra[f.key] = f.default;
        }
      });
      if (templateFieldOverrides[code]) {
        Object.assign(seedExtra, templateFieldOverrides[code]);
      }
      freshExtra = seedExtra;
      freshAttachments = (tpl?.attachments || []).map((a, i) => ({ id: i + 1, name: a.name, count: a.count }));
      freshTitle = tpl?.name ? `${businessLabel} ${tpl.name}` : "";
      setExtraFields(freshExtra);
      setAttachments(freshAttachments);
      setTitle(freshTitle);
    } else {
      setCategoryName(""); setItemName(""); setFieldName("");
      setExtraFields({});
      setAttachments([{ id: 1, name: "", count: 1 }]);
      setTitle("");
    }
    setItemList([]);
    setSalaryList([]);
    setGenericTableRows([]);
    setSupplyAmount(""); setVat("");
    // ✅ 자동생성 필드(제목/사용내역 내용) 수정 보호 상태도 새 종류를 고를 때마다
    // 초기화 — 이전 템플릿에서 손댔던 상태가 새 템플릿까지 넘어가면 안 된다.
    setTitleTouched(false);
    setItemNameTouched(false);
    // ✅ 새 종류를 고르는 건 새 기안을 새로 시작하는 것이므로, 이전 저장 시도의
    // 첨부파일 재시도 상태를 남겨두면 혼란만 준다 — 초기화.
    setSavedDraftId(null);
    setFailedUploads([]);
    setStep("form");

    if (code !== OTHER_CODE) {
      const freshValues = buildPreviewValues(code, {
        title: freshTitle,
        attachments: freshAttachments,
        itemList: [],
        salaryList: [],
        genericTableRows: [],
        extraFields: freshExtra,
        partnerList,
        totalAmount: 0,
        itemName: (categoryItemFieldDefaults[code] || {}).item_name || "",
        category,
      });
      openPreviewWindow("preview", code, freshValues);
    }
  }

  function backToPick() {
    setStep("pick");
  }

  // ── 이전 자료 불러오기 ────────────────────────────────────
  useEffect(() => {
    if (!showCopyModal || templateCode === OTHER_CODE) return;
    let ignore = false;
    (async () => {
      setCopyLoading(true);
      try {
        const { data } = await api.get(dataApiBase, {
          params: { template_code: templateCode, search: copySearch || undefined, ordering: "-draft_date,-seq" },
        });
        const list = Array.isArray(data) ? data : data.results || [];
        if (!ignore) setCopyList(list);
      } catch (err) {
        console.error("❌ 이전 자료 목록 불러오기 실패:", err);
      } finally {
        if (!ignore) setCopyLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [showCopyModal, copySearch, templateCode, dataApiBase]);

  function handleCopySelect(row) {
    setExecutionDate(row.execution_date || executionDate);
    setCategoryName(row[fieldNameMap.categoryName] || categoryName);
    setItemName(row[fieldNameMap.itemName] || itemName);
    setFieldName(row[fieldNameMap.fieldName] || fieldName);
    setSupplyAmount(String(row.supply_amount || 0));
    setVat(String(row.vat || 0));
    setPayMethodLabel(row.pay_method === "card" ? "카드결제" : row.pay_method === "cash" ? "현금결제" : "계좌이체");
    setRemark(row.remark || "");
    const copiedPartners = (row.partners?.length ? row.partners : row.partner_name ? [{ name: row.partner_name, id: row.partner_id }] : [])
      .map((p) => ({ name: p.name || "", id: p.id || null, show: false, highlight: -1 }));
    setPartnerList(copiedPartners.length ? copiedPartners : [{ name: "", id: null, show: false, highlight: -1 }]);
    setItemList((row.items || []).map((it) => ({ ...it })));
    setSalaryList((row.salaries || []).map((s) => ({ ...s })));
    setShowCopyModal(false);
    setCopySearch("");
  }

  // ── 붙임 ──────────────────────────────────────────────────
  const addAttachment = () => setAttachments((prev) => [...prev, { id: Date.now(), name: "", count: 1, file: null }]);
  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    // 실패 목록에 있던 행을 지우면 재시도 대상에서도 함께 제외
    setFailedUploads((prev) => prev.filter((a) => a.id !== id));
  };
  const setAttachmentField = (id, key, value) =>
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, [key]: value } : a)));

  // ✅ [파일 첨부] — 선택한 파일마다 새 붙임 행을 만든다(이름은 파일명으로 자동 채움,
  // 이후에도 수정 가능). 파일 자체는 저장 시점까지 업로드하지 않고 행에 들고만 있는다.
  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setAttachments((prev) => [
      ...prev,
      ...files.map((file, i) => ({ id: Date.now() + i, name: file.name, count: 1, file })),
    ]);
    e.target.value = ""; // 같은 파일을 다시 선택해도 onChange가 또 발생하도록 초기화
  };

  // ✅ 문서 ID를 대상으로 rows 중 실제 file이 있는 행들을 순차 업로드하고,
  // 실패한 행들만 배열로 돌려준다(성공 행은 목록에서 빠짐).
  async function uploadAttachmentFiles(draftId, rows) {
    const failures = [];
    for (const row of rows) {
      if (!row.file) continue;
      try {
        const formData = new FormData();
        formData.append("document", draftId);
        formData.append("file", row.file);
        await api.post("/draft/attachments/", formData);
      } catch (err) {
        console.error("❌ 첨부파일 업로드 실패:", row.file?.name, err.response?.data || err);
        failures.push(row);
      }
    }
    return failures;
  }

  async function retryFailedUploads() {
    if (!savedDraftId || failedUploads.length === 0) return;
    setUploadingFiles(true);
    const stillFailing = await uploadAttachmentFiles(savedDraftId, failedUploads);
    setUploadingFiles(false);
    setFailedUploads(stillFailing);
    if (stillFailing.length === 0) {
      previewChannelRef.current?.postMessage({ type: "clear" });
      try { new BroadcastChannel(listRefreshChannelName).postMessage({ type: "refresh" }); } catch {}
      alert("✅ 첨부파일까지 모두 업로드 완료!");
      navigate(listRoutePath);
    } else {
      alert(`⚠️ 여전히 ${stillFailing.length}개 파일 업로드에 실패했습니다.`);
    }
  }

  // ── 미리보기 values 매핑 ──────────────────────────────────
  const previewValues = useMemo(
    () => buildPreviewValues(templateCode, { title, attachments, itemList, salaryList, genericTableRows, extraFields, partnerList, totalAmount, itemName, category }),
    [templateCode, title, attachments, itemList, salaryList, genericTableRows, extraFields, partnerList, totalAmount, itemName, category]
  );

  // ✅ sessionStorage 기록 + BroadcastChannel 알림 — 팝업이 이미 열려있으면 이 호출만으로
  // 실시간 갱신된다(팝업이 없어도 그냥 아무도 안 듣는 메시지라 비용 없음).
  function syncPreviewData(code, values, documentNumber, approvalLine) {
    const payload = { templateCode: code, values, documentNumber, approvalLine };
    sessionStorage.setItem(previewStorageKey, JSON.stringify(payload));
    previewChannelRef.current?.postMessage({ type: "update", payload });
  }

  // ✅ 폼 입력 실시간 반영 — 300ms 디바운스로 syncPreviewData 호출(품목/인건비 표,
  // 거래처 입력 등 previewValues에 들어가는 모든 값 포함). "종류 다시 선택" 화면(pick)
  // 에서는 굳이 동기화하지 않는다(팝업이 열려있어도 갱신할 필요 없는 중간 상태).
  useEffect(() => {
    if (step !== "form" || templateCode === OTHER_CODE || !templateCode) return;
    const timer = setTimeout(() => {
      syncPreviewData(templateCode, previewValues, draftNo, approvers);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, templateCode, previewValues, draftNo, approvers]);

  // ── PDF 미리보기 팝업 열기/갱신 ──────────────────────────────
  // action: "preview"(열기/포커스) | "download"(연 뒤 자동으로 다운로드 버튼 클릭)
  //         | "print"(연 뒤 자동으로 인쇄 대화상자 호출)
  // overrideCode/overrideValues: pickTemplate에서 React state 반영 전에 "새로 고른
  // 템플릿" 기준으로 동기 호출할 때만 넘긴다. 생략하면 현재 templateCode/previewValues를 쓴다.
  //
  // ⚠️ 인쇄가 "새 창 경유"인 이유 — 이 작성 페이지 자체에는 문서 DOM이 없다(미리보기가
  // 실시간 동기화를 위해 완전히 별도 창으로 분리된 아키텍처, 위 syncPreviewData 참고).
  // window.print()는 항상 "현재 문서"를 인쇄하므로, 작성 페이지에서 그냥 호출하면 폼
  // 전체(버튼·패널 등)가 인쇄된다. 반면 새 창은 이미 문서(.preview-paper)만 렌더하고,
  // PreviewPaper가 공유하는 IncomeProposalPreview.css의 기존 @media print 규칙
  // (.preview-header 자동 숨김·A4 여백·직인 색상 보존)을 그대로 재사용할 수 있어
  // 별도 인쇄 전용 CSS를 새로 만들 필요가 없다 — 이 경로가 유일하게 성립하면서도 가장 간단.
  function openPreviewWindow(action = "preview", overrideCode, overrideValues) {
    const code = overrideCode ?? templateCode;
    const values = overrideValues ?? previewValues;
    if (code === OTHER_CODE || !code) {
      alert(`"${otherCodeLabel}"은 정형 템플릿이 없어 PDF 미리보기를 제공하지 않습니다.`);
      return;
    }
    syncPreviewData(code, values, draftNo, approvers);

    const path = `${previewRoutePath}${action !== "preview" ? `?action=${action}` : ""}`;
    // ⚠️ 팝업 차단 방지 — 이 함수가 클릭 이벤트의 동기 콜스택 안에서 호출되는 경우에만
    // (pickTemplate, 버튼 onClick) window.open이 차단되지 않는다. await/setTimeout 등
    // 비동기 처리 뒤에 호출하지 말 것.
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      // ✅ 같은 창 재사용(중복 창 방지) — 데이터는 위에서 이미 갱신 브로드캐스트했다.
      previewWindowRef.current.focus();
      if (action === "download" || action === "print") {
        previewWindowRef.current.location.search = `?action=${action}`;
      }
      setPreviewBlocked(false);
      return;
    }
    const win = window.open(path, previewWindowName, "width=900,height=1200");
    if (!win) {
      // 브라우저가 팝업을 막았다 — 안내 문구 + 수동 열기 버튼으로 폴백(그 버튼 클릭은
      // 새로운 사용자 제스처이므로 다시 시도하면 차단되지 않는다).
      setPreviewBlocked(true);
      return;
    }
    previewWindowRef.current = win;
    setPreviewBlocked(false);
  }

  // ── 저장 ──────────────────────────────────────────────────
  async function handleSubmit() {
    if (!executionDate) return alert("집행예정일을 입력해주세요.");
    const validVendor = partnerList.some((v) => v.name.trim() !== "");
    if (!validVendor) return alert("최소 1개 이상의 거래처가 필요합니다.");
    if (!title.trim()) return alert("제목을 입력해주세요.");

    // ✅ 품목 금액 vs 공급가액+부가세 정합성 — 차단이 아니라 경고(의도적 보정 허용,
    // ExpenseItemPanel의 수동 보정으로 생길 수 있는 불일치).
    const mismatchRows = itemList
      .map((it, i) => ({ ...it, no: i + 1 }))
      .filter((it) => Number(it.total || 0) !== Number(it.supply || 0) + Number(it.vat || 0));
    if (mismatchRows.length) {
      const detail = mismatchRows
        .map((it) => `품목 ${it.no}행의 금액과 공급가액+부가세 합이 일치하지 않습니다.`)
        .join("\n");
      if (!window.confirm(`${detail}\n그대로 저장할까요?`)) return;
    }

    if (approvers.length === 0) {
      alert("결재선을 등록해주세요.");
      return;
    }
    setShowApprovalConfirm(true);
  }

  async function doSubmit() {
    setShowApprovalConfirm(false);
    setSaving(true);
    try {
      const cleanedPartners = partnerList.filter((p) => p.name.trim() !== "").map((p) => ({ name: p.name, id: p.id }));
      const approval_lines_data = approvers.map((a, idx) => ({
        approver_id: a.id,
        approval_type: a.status || "결재",
        order: idx + 1,
        position: a.position || "",
      }));

      const isOther = templateCode === OTHER_CODE;
      const payload = {
        draft_date: draftDate,
        execution_date: executionDate,
        partner_name: partnerList[0]?.name || "",
        partner_id: partnerList[0]?.id || null,
        partners: cleanedPartners,
        // ✅ 유류비(세차비) 기본 2행 중 사용자가 안 채운(금액 0) 행은 저장 데이터에
        // 0원 품목으로 남기지 않는다(2026-07-30 A안). 다른 템플릿은 기존 그대로.
        items: templateCode === "fuel_cost" ? itemList.filter((it) => Number(it.total || 0) > 0) : itemList,
        salaries: salaryList,
        [fieldNameMap.categoryName]: categoryName,
        [fieldNameMap.itemName]: itemName,
        [fieldNameMap.fieldName]: fieldName,
        supply_amount: Number(supplyAmount) || 0,
        vat: Number(vat) || 0,
        pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
        remark,
        draft_document: {
          document_type: "expense_draft",
          category,
          subcategory: isOther ? "기타" : (EXPENSE_TEMPLATES[templateCode]?.name || ""),
          detail_option: isOther ? "" : templateCode,
          template_code: isOther ? "" : templateCode,
          // ✅ 전 사업 확산 [5] — GenericDetailTablePanel의 행은 저장할 별도 모델
          // 필드가 없어(카페 템플릿마다 열 구성이 달라 items/salaries 스키마로 못
          // 담음) template_data(자유 JSON) 안에 곁다리 키로 함께 저장한다. 기존
          // 사업들의 template_data는 이 키가 없을 뿐 그대로 extraFields 형태라
          // 읽는 쪽(ExpenseDraftDetailModal)엔 영향 없음.
          template_data: isOther ? null : (
            genericTableRows.length ? { ...extraFields, __genericTableRows: genericTableRows } : extraFields
          ),
          title,
          draft_date: draftDate,
          work_month: draftDate.slice(0, 7),
          partner_name: partnerList[0]?.name || "",
          partner: partnerList[0]?.id || null,
          amount: totalAmount,
          pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
          desc: remark,
          attachment_items: attachments.filter((a) => a.name.trim()).map((a) => ({ name: a.name, count: a.count })),
          seq,
          draft_no: draftNo,
          approval_lines_data,
        },
      };

      const { data } = await api.post(combinedCreateApi, payload);
      await api.post(`/draft/documents/${data.draft_document.id}/submit/`);
      const draftId = data.draft_document.id;
      setSavedDraftId(draftId);

      // ✅ ① 기안 문서 생성(위에서 완료) → ② 보관 중이던 파일 업로드. 문서는 이미
      // 저장됐으니 파일 업로드가 일부/전부 실패해도 문서를 다시 만들지 않고, 이
      // 화면에 남아 실패한 파일만 재시도할 수 있게 한다(아래 failedUploads 배너).
      const fileRows = attachments.filter((a) => a.file);
      if (fileRows.length > 0) {
        setUploadingFiles(true);
        const failures = await uploadAttachmentFiles(draftId, fileRows);
        setUploadingFiles(false);
        setFailedUploads(failures);
        if (failures.length > 0) {
          alert(
            `⚠️ 기안 문서는 저장됐지만 파일 ${failures.length}개 업로드에 실패했습니다.\n` +
            `기안번호: ${data.draft_document.draft_no}\n아래에서 다시 시도해주세요.` +
            (draftNumberChangedLine(data.draft_number) ? `\n\n${draftNumberChangedLine(data.draft_number)}` : "")
          );
          return; // 목록으로 이동하지 않고 이 화면에 남아 재시도 UI를 보여준다
        }
      }

      previewChannelRef.current?.postMessage({ type: "clear" });
      // ✅ keep-alive 탭 전환에서는 목록 페이지가 마운트를 유지한 채 재사용되므로
      // 저장 성공 신호로 직접 재조회하게 한다(마운트 시 1회 fetch만으로는 새 건이 안 보임).
      try {
        new BroadcastChannel(listRefreshChannelName).postMessage({ type: "refresh" });
      } catch {}
      const changed = draftNumberChangedLine(data.draft_number);
      alert(`✅ 저장 및 상신 완료!\n기안번호: ${data.draft_document.draft_no}${changed ? `\n\n${changed}` : ""}`);
      navigate(listRoutePath);
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert(formatDraftSaveError(err));
    } finally {
      setSaving(false);
    }
  }

  // ── 렌더 ──────────────────────────────────────────────────
  if (step === "pick") {
    return (
      <div style={{ padding: 16 }}>
        <h2>{businessLabel} 지출데이터+기안 작성</h2>
        <p style={{ color: "#666", fontSize: 13 }}>지출 종류를 선택하세요. 데이터 입력과 기안 문서가 함께 만들어집니다.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, maxWidth: 900 }}>
          {templates.map((t) => (
            <button
              key={t.code}
              onClick={() => pickTemplate(t.code)}
              style={{ ...PICK_BTN_STYLE, border: "1px solid #ccc", background: "#fff" }}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={() => pickTemplate(OTHER_CODE)}
            style={{ ...PICK_BTN_STYLE, border: "1px dashed #999", background: "#fafafa" }}
          >
            {otherCodeLabel}
          </button>
        </div>
      </div>
    );
  }

  const tpl = templateCode !== OTHER_CODE ? EXPENSE_TEMPLATES[templateCode] : null;
  const showItemPanel = itemPanelTemplates.includes(templateCode);
  const showSalaryPanel = salaryPanelTemplates.includes(templateCode);
  // ✅ 전 사업 확산 [5] — config.genericTablePanelTemplates에 명시적으로 등록된
  // template_code만 GenericDetailTablePanel을 보여준다(카페 4개점의 포스임대료/
  // 사회보험료 등, 사업마다 표 열 구성이 제각각이라 전용 패널 대신 범용 패널 사용).
  // 자동 감지가 아니라 opt-in 목록인 이유는 expenseDraftConfig.js 주석 참고.
  const genericTableField = genericTablePanelTemplates.includes(templateCode)
    ? (tpl?.fields || []).find((f) => f.key === "detail" && f.type === "table")
    : null;
  // "기타"를 제외하고, 위 두 패널이 쓰이지 않는 템플릿에 한해 generic 부가 필드를 노출
  // ✅ payroll의 payDate는 위에서 전용 달력 입력으로 별도 렌더(값도 formatPayDate로
  // 가공)하므로, 여기 범용 자유입력 행에서는 제외 — 안 하면 같은 필드가 두 번 보인다.
  // fuel_cost의 detail(세부내역)도 마찬가지로 아래에서 품목 표 기반 자동생성
  // 읽기전용 행으로 별도 렌더. utility_fee의 dueDate는 연/월/일 분리 칸
  // (DueDateSplitFields)으로 별도 렌더하므로 여기서 제외.
  const genericExtraFields = (tpl?.fields || []).filter(
    (f) => f.type !== "table" && !["title", "amount", "vendor"].includes(f.key)
      && !(templateCode === "payroll" && f.key === "payDate")
      && !(templateCode === "fuel_cost" && f.key === "detail")
      && !(templateCode === "utility_fee" && f.key === "dueDate")
  );
  const totalAttachedFiles = attachments.filter((a) => a.file).length;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>
          {businessLabel} 지출데이터+기안 작성 — {templateCode === OTHER_CODE ? otherCodeLabel : tpl?.name}
        </h2>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={backToPick} style={HEADER_BTN_STYLE}>← 종류 다시 선택</button>
          {templateCode !== OTHER_CODE && (
            <>
              <button onClick={() => openPreviewWindow("print")} style={HEADER_BTN_STYLE}>
                🖨 기안 인쇄
              </button>
              <button onClick={() => openPreviewWindow("preview")} style={HEADER_BTN_STYLE}>
                👁️ PDF 미리보기
              </button>
              <button onClick={() => openPreviewWindow("download")} style={HEADER_BTN_STYLE}>
                📥 PDF 다운로드
              </button>
            </>
          )}
          <button
            onClick={savedDraftId ? retryFailedUploads : handleSubmit}
            disabled={saving || uploadingFiles}
            style={{
              ...SAVE_HEADER_BTN_STYLE,
              marginLeft: 12,
              background: saving || uploadingFiles ? "#999" : SAVE_HEADER_BTN_STYLE.background,
              cursor: saving || uploadingFiles ? "default" : "pointer",
            }}
          >
            {uploadingFiles ? "파일 업로드 중…" : savedDraftId ? "첨부파일 다시 시도" : saving ? "저장 중…" : "저장 및 상신"}
          </button>
        </div>
      </div>

      {failedUploads.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
          padding: "8px 12px", background: "#fde8e8", border: "1px solid #f5b5b5", borderRadius: 6,
          color: "#7a1f1f", fontSize: 13,
        }}>
          <span>
            ⚠️ 기안 문서는 저장됐지만 다음 파일 업로드에 실패했습니다: {failedUploads.map((f) => f.name).join(", ")}
          </span>
          <button
            onClick={retryFailedUploads}
            disabled={uploadingFiles}
            style={{ ...HEADER_BTN_STYLE, background: "#7a1f1f", color: "#fff", border: "none" }}
          >
            {uploadingFiles ? "재시도 중…" : "다시 시도"}
          </button>
        </div>
      )}

      {previewBlocked && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
          padding: "8px 12px", background: "#fff3cd", border: "1px solid #ffe08a", borderRadius: 6,
          color: "#664d03", fontSize: 13,
        }}>
          <span>브라우저가 미리보기 팝업 창을 차단했습니다. 주소창의 팝업 차단 아이콘을 허용하거나, 아래 버튼으로 다시 열어주세요.</span>
          <button
            onClick={() => openPreviewWindow("preview")}
            style={{ ...HEADER_BTN_STYLE, background: "#664d03", color: "#fff", border: "none" }}
          >
            미리보기 창 열기
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 16 }}>
        {/* 왼쪽: 입력 폼 — Row 자체가 ExpenseBusinessNew와 동일하게 marginBottom:10을
            가지므로 여기서 별도 gap을 주면 간격이 배로 벌어진다. */}
        <div style={{ flex: "0 0 420px", display: "flex", flexDirection: "column" }}>
          {templateCode !== OTHER_CODE && (
            <button onClick={() => setShowCopyModal(true)} style={FORM_WIDTH_BTN_STYLE}>
              📋 이전 자료 불러오기
            </button>
          )}
          <Row label="기안번호(자동)"><input value={draftNo} readOnly style={ROW_INPUT_READONLY_STYLE} /></Row>
          <Row label="작성일">
            <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} style={ROW_INPUT_STYLE} />
          </Row>
          <Row label="집행예정일">
            <input type="date" value={executionDate} onChange={(e) => setExecutionDate(e.target.value)} style={ROW_INPUT_STYLE} />
          </Row>
          <Row label={TITLE_AUTOGEN_TEMPLATES.includes(templateCode) ? "제목(자동완성)" : "제목"}>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (TITLE_AUTOGEN_TEMPLATES.includes(templateCode)) setTitleTouched(true);
              }}
              style={TITLE_AUTOGEN_TEMPLATES.includes(templateCode) && !titleTouched ? ROW_INPUT_AUTO_STYLE : ROW_INPUT_STYLE}
            />
          </Row>

          <Row label="사용내역 구분"><input value={categoryName} onChange={(e) => setCategoryName(e.target.value)} style={ROW_INPUT_STYLE} /></Row>
          <Row label="사용내역 내용">
            {templateCode === "fuel_cost" ? (
              <input
                value={itemName}
                onChange={(e) => { setItemName(e.target.value); setItemNameTouched(true); }}
                style={itemNameTouched ? ROW_INPUT_STYLE : ROW_INPUT_AUTO_STYLE}
              />
            ) : (
              <input
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                readOnly={showItemPanel || showSalaryPanel}
                style={showItemPanel || showSalaryPanel ? ROW_INPUT_READONLY_STYLE : ROW_INPUT_STYLE}
              />
            )}
          </Row>
          <Row label="사용내역 분야"><input value={fieldName} onChange={(e) => setFieldName(e.target.value)} style={ROW_INPUT_STYLE} /></Row>

          {/* ✅ 유류비 전용 — 차량 한 칸(번호+차종 통합, "104머9770(코란도)" 형식).
              부분일치 자동완성(공용 VEHICLES 목록) 보조 + 목록에 없는 차량(렌트·
              신규 등)은 "번호(차종)" 형식으로 직접 입력(괄호 없으면 전체가 번호,
              차종은 빈값). 기본값은 사업별로 다르다(경영팀/용역은 없음, 환경종합/
              환경소독은 config의 templateFieldOverrides가 채움). */}
          {templateCode === "fuel_cost" && (
            <Row label="차량">
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  value={extraFields.carInput ?? ""}
                  onChange={(e) => handleCarInputChange(e.target.value)}
                  onFocus={() => setCarSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setCarSuggestOpen(false), 150)}
                  onKeyDown={handleCarKeyDown}
                  // ✅ 사업별 안내 문구 — 소독/종합처럼 templateFieldOverrides.fuel_cost.carInput이
                  // 있으면 그 차량을 예시로(어차피 기본값으로 이미 채워져 실제로 보일 일은
                  // 드물지만, 사용자가 지운 경우엔 이 문구가 뜬다). 경영팀/용역(오버라이드 없음)은
                  // 기존 코란도 예시 문구 그대로.
                  placeholder={`예: ${templateFieldOverrides.fuel_cost?.carInput || "104머9770(코란도)"}`}
                  style={ROW_INPUT_STYLE}
                  autoComplete="off"
                />
                {carSuggestOpen && carSuggestVehicles.length > 0 && (
                  <ul style={CAR_SUGGEST_LIST_STYLE}>
                    {carSuggestVehicles.map((v, i) => (
                      <li
                        key={v.number}
                        style={i === carHighlight ? { ...CAR_SUGGEST_ITEM_STYLE, background: "#f0f9ff" } : CAR_SUGGEST_ITEM_STYLE}
                        onMouseDown={() => selectVehicle(v)}
                      >
                        {v.number}({v.model})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Row>
          )}

          {/* ✅ "월" 표기 전용 입력 — 연/월 분리 칸(YearMonthSplitFields). utility_fee/
              credit_card는 "YYYY년 M월"(연도 필수, showYear=true) — 나머지(사무실관리비/
              전기료/퇴직연금/정수기)는 연도 없이 "M월"만(showYear=false). */}
          {["office_maintenance", "electric_bill", "pension", "water_rental", "utility_fee", "credit_card"].includes(templateCode) && (() => {
            const needsYear = ["utility_fee", "credit_card"].includes(templateCode);
            const monthKey = templateCode === "office_maintenance" ? "maintMonth"
              : templateCode === "electric_bill" ? "elecMonth"
              : templateCode === "pension" ? "pensionMonth"
              : templateCode === "utility_fee" ? "utilityMonth"
              : templateCode === "credit_card" ? "cardMonth"
              : "waterMonth";
            return (
              <YearMonthSplitFields
                label="납부월"
                value={extraFields[monthKey] ?? ""}
                onChange={(v) => setExtraField(monthKey, v)}
                showYear={needsYear}
              />
            );
          })()}

          {/* ✅ 기장수수료 전용 — 집행월(연+월 분리 칸). */}
          {templateCode === "accounting_fee" && (
            <YearMonthSplitFields
              label="집행월"
              value={extraFields.executionMonth ?? ""}
              onChange={(v) => setExtraField("executionMonth", v)}
            />
          )}

          {/* ✅ 임직원급여 전용 — 지급일(달력). 이 값에서 "지급일" 표시(formatPayDate)와
              도입문의 "월"(formatYearMonth)을 함께 파생시킨다(구 페이지 payDateRaw와
              동일 역할) — genericExtraFields의 payDate 자유입력 행은 아래에서 제외. */}
          {templateCode === "payroll" && (
            <Row label="지급일">
              <input
                type="date"
                value={extraFields.payDate ?? ""}
                onChange={(e) => setExtraField("payDate", e.target.value)}
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}

          {/* ✅ 기부금 전용 — 기부사유 + 기부처(구 페이지 donationReason/donationTarget). */}
          {templateCode === "donation" && (
            <>
              <Row label="기부사유">
                <input
                  value={extraFields.donationReason ?? ""}
                  onChange={(e) => setExtraField("donationReason", e.target.value)}
                  placeholder="예: 독립유공자의 희생과 공헌을 기리기 위해"
                  style={ROW_INPUT_STYLE}
                />
              </Row>
              <Row label="기부처">
                <input
                  value={extraFields.donationTarget ?? ""}
                  onChange={(e) => setExtraField("donationTarget", e.target.value)}
                  placeholder="예: 사단법인 독립유공자유족회"
                  style={ROW_INPUT_STYLE}
                />
              </Row>
            </>
          )}

          {/* ✅ 법인인감증명서 전용 — 지출사유(구 페이지 sealReason). */}
          {templateCode === "corporate_seal" && (
            <Row label="지출사유">
              <input
                value={extraFields.sealReason ?? ""}
                onChange={(e) => setExtraField("sealReason", e.target.value)}
                placeholder="예: 은행 계좌 개설"
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}

          {/* ✅ 사업자등록증/영업신고수수료 전용 — 발급대상(구 페이지 bizTarget, 두
              템플릿이 같은 상태를 공유). 영업신고수수료는 업종(bizCategory)도 추가. */}
          {(templateCode === "biz_license" || templateCode === "biz_report") && (
            <Row label="발급대상">
              <input
                value={extraFields.bizTarget ?? ""}
                onChange={(e) => setExtraField("bizTarget", e.target.value)}
                placeholder="예: 신규 카페인 그린점"
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}
          {templateCode === "biz_report" && (
            <Row label="업종">
              <input
                value={extraFields.bizCategory ?? ""}
                onChange={(e) => setExtraField("bizCategory", e.target.value)}
                placeholder="예: 식품접객업"
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}

          {/* ✅ 주차비용 전용 — 지점 + 발생사유(구 페이지 parkingBranch/parkingReason). */}
          {templateCode === "parking_fee" && (
            <>
              <Row label="지점">
                <input
                  value={extraFields.parkingBranch ?? ""}
                  onChange={(e) => setExtraField("parkingBranch", e.target.value)}
                  placeholder="예: 가산점"
                  style={ROW_INPUT_STYLE}
                />
              </Row>
              <Row label="발생사유">
                <input
                  value={extraFields.parkingReason ?? ""}
                  onChange={(e) => setExtraField("parkingReason", e.target.value)}
                  placeholder="예: 통장을 개설함"
                  style={ROW_INPUT_STYLE}
                />
              </Row>
            </>
          )}

          {/* ✅ 직무교육비 전용 — 수강자(구 페이지 trainee). course/schedule/target/
              institution은 tpl.fields에 이미 선언돼 있어 아래 범용 입력에서 처리된다. */}
          {templateCode === "job_training" && (
            <Row label="수강자">
              <input
                value={extraFields.trainee ?? ""}
                onChange={(e) => setExtraField("trainee", e.target.value)}
                placeholder="예: 사무직원(윤길상)"
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}

          {/* ✅ 배너광고 전용 — 광고대상(구 페이지 adTarget). */}
          {templateCode === "banner_ad" && (
            <Row label="광고대상">
              <input
                value={extraFields.adTarget ?? ""}
                onChange={(e) => setExtraField("adTarget", e.target.value)}
                placeholder="예: 카페사업"
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}

          {/* ✅ 차량할부금 전용 — 회차(구 페이지 installmentRound, 매달 반복되는
              문서라 이 값만 바뀜). 제목 자동생성에 사용. */}
          {templateCode === "car_installment" && (
            <Row label="회차">
              <input
                inputMode="numeric"
                value={extraFields.installmentRound ?? ""}
                onChange={(e) => setExtraField("installmentRound", e.target.value.replace(/[^\d]/g, ""))}
                placeholder="예: 15"
                style={ROW_INPUT_STYLE}
              />
            </Row>
          )}

          {/* ✅ 유류비 전용 — 세부내역 자동 조립(품목 표에서 파생, 읽기전용). */}
          {templateCode === "fuel_cost" && (
            <Row label="세부내역">
              <input value={buildFuelDetail(itemList)} readOnly style={ROW_INPUT_READONLY_STYLE} />
            </Row>
          )}

          {/* ✅ 인터넷·전화요금 전용 — 납기일(연/월/일 분리 칸, "YYYY-MM-DD" 조립). */}
          {templateCode === "utility_fee" && (
            <DueDateSplitFields
              label="납기일"
              value={extraFields.dueDate ?? ""}
              onChange={(v) => setExtraField("dueDate", v)}
            />
          )}

          {genericExtraFields.map((f) => (
            <Row label={f.label.replace(/\s+/g, "")} key={f.key}>
              {f.multiline ? (
                <AutoResizeTextarea
                  value={extraFields[f.key] ?? ""}
                  onChange={(e) => setExtraField(f.key, e.target.value)}
                  style={ROW_TEXTAREA_STYLE}
                />
              ) : (
                <input
                  value={extraFields[f.key] ?? ""}
                  onChange={(e) => setExtraField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  style={ROW_INPUT_STYLE}
                />
              )}
            </Row>
          ))}

          <Row label="공급가액"><input value={supplyAmount} onChange={(e) => setSupplyAmount(e.target.value.replace(/[^\d]/g, ""))} readOnly={showItemPanel || showSalaryPanel} style={showItemPanel || showSalaryPanel ? ROW_INPUT_READONLY_STYLE : ROW_INPUT_STYLE} /></Row>
          <Row label="부가세"><input value={vat} onChange={(e) => setVat(e.target.value.replace(/[^\d]/g, ""))} readOnly={showItemPanel || showSalaryPanel} style={showItemPanel || showSalaryPanel ? ROW_INPUT_READONLY_STYLE : ROW_INPUT_STYLE} /></Row>
          <Row label="합계(자동)"><input value={totalAmount.toLocaleString()} readOnly style={ROW_INPUT_READONLY_STYLE} /></Row>

          <Row label="거래방법">
            <div style={{ display: "flex", gap: 8, alignItems: "center", height: 36 }}>
              {PAY_METHOD_LABELS.map((label) => (
                <label key={label} style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <input type="radio" checked={payMethodLabel === label} onChange={() => setPayMethodLabel(label)} /> {label}
                </label>
              ))}
            </div>
          </Row>

          <Row label="비고"><AutoResizeTextarea value={remark} onChange={(e) => setRemark(e.target.value)} style={ROW_TEXTAREA_STYLE} /></Row>
        </div>

        {/* 오른쪽: 거래처/품목/인건비 패널 + 붙임/결재라인(2단) — 미리보기가 새 창으로
            빠지면서 폭을 넉넉히 사용 */}
        <div style={{ flex: 1, minWidth: 480 }}>
          <VendorPanel vendors={partners} loadingVendors={loadingVendors} onChange={setPartnerList} />
          {showItemPanel && (
            <ExpenseItemPanel
              key={templateCode}
              itemCodes={itemCodes}
              onItemsChange={setItemList}
              initialItems={
                templateCode === "fuel_cost" ? FUEL_COST_INITIAL_ITEMS
                : templateCode === "utility_fee" ? UTILITY_FEE_INITIAL_ITEMS
                : undefined
              }
              autoUnitRules={templateCode === "fuel_cost" ? [{ match: "세차", unit: "식" }] : undefined}
            />
          )}
          {showSalaryPanel && <SalaryPanel draftDate={draftDate} onItemsChange={setSalaryList} />}
          {genericTableField && (
            <GenericDetailTablePanel key={templateCode} columns={genericTableField.columns} onRowsChange={setGenericTableRows} />
          )}
          {templateCode === OTHER_CODE && (
            <div style={{ padding: 16, border: "1px dashed #ccc", borderRadius: 8, color: "#666", marginTop: 8 }}>
              "{otherCodeLabel}"은 정형 템플릿이 없어 PDF 미리보기를 제공하지 않습니다. 제목/비고/붙임 그대로 저장됩니다.
            </div>
          )}

          <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
            {/* 붙임 */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <strong>붙임</strong>
                <span style={{
                  fontSize: 12, fontWeight: 600, color: totalAttachedFiles > 0 ? "#1e7b34" : "#999",
                  paddingLeft: 10, borderLeft: "1px solid #ddd", lineHeight: `${ATTACHMENT_ROW_HEIGHT}px`,
                }}>
                  첨부 파일 총 {totalAttachedFiles}건
                </span>
                <button
                  type="button"
                  onClick={addAttachment}
                  style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_ROW_HEIGHT, color: BTN_TEXT_COLOR, marginLeft: 4 }}
                  title="텍스트로만 추가"
                >
                  +
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFilesSelected}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ ...HEADER_BTN_STYLE, ...ATTACHMENT_ROW_ITEM_STYLE, padding: "0 12px", fontSize: 13 }}
                >
                  📎 파일 첨부
                </button>
              </div>
              {attachments.map((a) => (
                <div key={a.id} style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
                  <input
                    value={a.name}
                    onChange={(e) => setAttachmentField(a.id, "name", e.target.value)}
                    style={{ ...ATTACHMENT_ROW_ITEM_STYLE, flex: 1, minWidth: 0, position: "relative", top: 2 }}
                    placeholder="붙임 이름"
                  />
                  <span style={{
                    ...ATTACHMENT_ROW_ITEM_STYLE,
                    width: ATTACHMENT_STATUS_COL_WIDTH, flexShrink: 0, textAlign: "right",
                    fontSize: 12, fontWeight: a.file ? 600 : 400, color: a.file ? "#1e7b34" : "#999", whiteSpace: "nowrap",
                    lineHeight: `${ATTACHMENT_ROW_HEIGHT}px`,
                  }}>
                    첨부 파일 {a.file ? 1 : 0}건
                  </span>
                  <div style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_ACTION_COL_WIDTH, flexShrink: 0 }}>
                    {a.file ? (
                      <label
                        title="클릭하여 파일 재선택"
                        style={{
                          ...ATTACHMENT_ROW_ITEM_STYLE,
                          display: "flex", alignItems: "center", gap: 6, width: "100%",
                          padding: "0 10px",
                          background: "#e6f4ea", border: "1px solid #a6d8b3", borderRadius: 6,
                          color: "#1e7b34", fontSize: 12, cursor: "pointer",
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>✓</span>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {a.file.name}
                        </span>
                        <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>({formatFileSize(a.file.size)})</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttachmentField(a.id, "file", null); }}
                          style={{ width: 16, height: 16, fontSize: 10, lineHeight: 1, padding: 0, color: "#1e7b34", flexShrink: 0 }}
                          title="첨부 파일만 제거"
                        >
                          ✕
                        </button>
                        <input
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setAttachmentField(a.id, "file", file);
                            e.target.value = "";
                          }}
                          style={{ display: "none" }}
                        />
                      </label>
                    ) : (
                      <label
                        style={{
                          ...HEADER_BTN_STYLE,
                          ...ATTACHMENT_ROW_ITEM_STYLE,
                          width: "100%",
                          padding: "0 10px",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                        }}
                      >
                        📎 파일 첨부
                        <input
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setAttachmentField(a.id, "file", file);
                            e.target.value = "";
                          }}
                          style={{ display: "none" }}
                        />
                      </label>
                    )}
                  </div>
                  <input
                    type="number"
                    value={a.count}
                    onChange={(e) => setAttachmentField(a.id, "count", Number(e.target.value) || 1)}
                    style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_COUNT_COL_WIDTH, flexShrink: 0, position: "relative", top: 2 }}
                  />
                  <span style={{ width: ATTACHMENT_UNIT_COL_WIDTH, flexShrink: 0, lineHeight: `${ATTACHMENT_ROW_HEIGHT}px` }}>부</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_REMOVE_COL_WIDTH, color: BTN_TEXT_COLOR, flexShrink: 0 }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* 결재라인 — ApprovalLine.jsx 자체는 여러 페이지가 공유하는 범용
                컴포넌트라 내부는 width:100%(부모에 맞춤)로 설계돼 있다. 그대로
                flex:1을 주면 붙임과 동일하게 남는 폭을 다 먹어 카드가 불필요하게
                넓어지므로, 여기(공통 컴포넌트)에서만 내용에 맞는 고정폭으로
                제한한다 — 순번/이름/직책/기안·결재·합의 버튼/X가 자연스럽게
                들어가는 520px. 붙임은 flex:1 그대로라 남는 폭을 대신 흡수한다. */}
            <div style={{ flex: "0 0 520px" }}>
              <ApprovalLine
                approvers={approvers}
                setApprovers={setApprovers}
                onOpenModal={() => setShowApprovalModal(true)}
                documentType="expense_draft"
              />
            </div>
          </div>
        </div>
      </div>

      {showApprovalModal && (
        <ApprovalLineModal
          onClose={() => setShowApprovalModal(false)}
          onSelect={(selected) => { setApprovers(selected); setShowApprovalModal(false); }}
          documentType="expense_draft"
          initialApprovers={approvers}
        />
      )}
      {showApprovalConfirm && (
        <ApprovalLineConfirmDialog approvers={approvers} onCancel={() => setShowApprovalConfirm(false)} onConfirm={doSubmit} />
      )}

      {showCopyModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          {...copyBackdrop}
        >
          <div style={{ background: "#fff", borderRadius: 8, padding: 16, width: 700, maxHeight: "80vh", overflowY: "auto" }}>
            <h3>📋 이전 자료 불러오기 — {tpl?.name} 종류의 과거 건만 표시</h3>
            <input
              placeholder="기안번호·거래처·제목 검색"
              value={copySearch}
              onChange={(e) => setCopySearch(e.target.value)}
              style={{ width: "100%", marginBottom: 8, padding: 6 }}
            />
            {copyLoading ? (
              <div>불러오는 중…</div>
            ) : copyList.length === 0 ? (
              <div style={{ color: "#999" }}>같은 종류의 과거 자료가 없습니다.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th>작성일</th><th>기안번호</th><th>거래처</th><th>금액</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {copyList.map((row) => (
                    <tr key={row.id}>
                      <td>{row.draft_date}</td>
                      <td>{row.draft_no}</td>
                      <td>{row.partners?.length ? row.partners.map((p) => p.name).join(", ") : row.partner_name}</td>
                      <td style={{ textAlign: "right" }}>{(Number(row.total_amount) || 0).toLocaleString()}원</td>
                      <td><button onClick={() => handleCopySelect(row)} style={{ color: BTN_TEXT_COLOR }}>선택</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={ROW_GRID_STYLE}>
      <div style={ROW_LABEL_STYLE}>{label}</div>
      <div style={{ minWidth: 0, position: "relative" }}>{children}</div>
    </div>
  );
}

const SPLIT_ROW_STYLE = { display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" };
const SPLIT_NUM_STYLE = {
  flex: "0 0 auto", width: 40, height: 36, padding: "0 6px", border: "1px solid #d1d5db",
  borderRadius: 6, fontSize: 14, textAlign: "center", boxSizing: "border-box", background: "#fff",
};
const SPLIT_YEAR_STYLE = { ...SPLIT_NUM_STYLE, width: 56 };
const SPLIT_UNIT_STYLE = { fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" };

// ✅ 납부월류 분리 입력(연+월, showYear=false면 월만) — draft_document/DraftDocumentNew.jsx의
// YearMonthFields와 동일한 파싱/재동기화 로직(parseFlexibleYearMonth import)이지만, 이
// 페이지 자체의 Row/입력 스타일로 렌더한다(CSS 모듈 기반 구 페이지 스타일을 그대로
// 가져오면 이 페이지의 인라인 스타일 톤과 어긋나므로 로직만 재사용, 마크업은 새로).
function YearMonthSplitFields({ label, value, onChange, showYear = true }) {
  const lastEmitted = useRef(value);
  const initial = parseFlexibleYearMonth(value) || {};
  const [year, setYear] = useState(String(initial.year ?? (showYear ? new Date().getFullYear() : "")));
  const [month, setMonth] = useState(initial.month != null ? String(initial.month) : "");

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parseFlexibleYearMonth(value) || {};
    setYear(String(parsed.year ?? (showYear ? new Date().getFullYear() : "")));
    setMonth(parsed.month != null ? String(parsed.month) : "");
    lastEmitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextYear, nextMonth) => {
    const m = parseInt(nextMonth, 10);
    const assembled = !m ? "" : showYear ? `${nextYear || new Date().getFullYear()}년 ${m}월` : `${m}월`;
    lastEmitted.current = assembled;
    onChange(assembled);
  };

  return (
    <Row label={label}>
      <div style={SPLIT_ROW_STYLE}>
        {showYear && (
          <>
            <input
              inputMode="numeric" maxLength={4} value={year}
              onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ""); setYear(v); emit(v, month); }}
              style={SPLIT_YEAR_STYLE}
            />
            <span style={SPLIT_UNIT_STYLE}>년</span>
          </>
        )}
        <input
          inputMode="numeric" maxLength={2} value={month}
          onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ""); setMonth(v); emit(year, v); }}
          style={SPLIT_NUM_STYLE}
        />
        <span style={SPLIT_UNIT_STYLE}>월</span>
      </div>
    </Row>
  );
}

// ✅ 납기일류 분리 입력(연+월+일 → "YYYY-MM-DD") — 위와 동일 원칙(로직 재사용, 마크업 새로).
function DueDateSplitFields({ label, value, onChange }) {
  const lastEmitted = useRef(value);
  const initial = parseFlexibleDate(value) || {};
  const [year, setYear] = useState(String(initial.year ?? new Date().getFullYear()));
  const [month, setMonth] = useState(initial.month != null ? String(initial.month) : "");
  const [day, setDay] = useState(initial.day != null ? String(initial.day) : "");

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parseFlexibleDate(value) || {};
    setYear(String(parsed.year ?? new Date().getFullYear()));
    setMonth(parsed.month != null ? String(parsed.month) : "");
    setDay(parsed.day != null ? String(parsed.day) : "");
    lastEmitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextYear, nextMonth, nextDay) => {
    const pad = (n) => String(n).padStart(2, "0");
    const assembled = !nextMonth || !nextDay ? "" : `${nextYear || new Date().getFullYear()}-${pad(nextMonth)}-${pad(nextDay)}`;
    lastEmitted.current = assembled;
    onChange(assembled);
  };

  return (
    <Row label={label}>
      <div style={SPLIT_ROW_STYLE}>
        <input
          inputMode="numeric" maxLength={4} value={year}
          onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ""); setYear(v); emit(v, month, day); }}
          style={SPLIT_YEAR_STYLE}
        />
        <span style={SPLIT_UNIT_STYLE}>년</span>
        <input
          inputMode="numeric" maxLength={2} value={month}
          onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ""); setMonth(v); emit(year, v, day); }}
          style={SPLIT_NUM_STYLE}
        />
        <span style={SPLIT_UNIT_STYLE}>월</span>
        <input
          inputMode="numeric" maxLength={2} value={day}
          onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ""); setDay(v); emit(year, month, v); }}
          style={SPLIT_NUM_STYLE}
        />
        <span style={SPLIT_UNIT_STYLE}>일</span>
      </div>
    </Row>
  );
}

// ✅ 내용 줄 수에 따라 세로로 자동 확장되는 textarea(scrollHeight 기반, 최소 2줄).
// 예산과목 등 자동 채움 값도(useEffect가 value 변경을 감지) 동일하게 적용된다.
function AutoResizeTextarea({ value, onChange, style, minRows = 2, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      onChange={onChange}
      style={{ ...style, overflow: "hidden", resize: "none" }}
      {...rest}
    />
  );
}
